import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Crown, Flame, Trophy, Dumbbell, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SORTS = [
  { key: "signin_streak", label: "Sign-In Streak", icon: Flame, suffix: "d" },
  { key: "activity_streak", label: "Activity Streak", icon: Flame, suffix: "d" },
  { key: "badges", label: "Badges", icon: Trophy, suffix: "" },
  { key: "workouts", label: "Workouts", icon: Dumbbell, suffix: "" },
];

export default function FriendsLeaderboard() {
  const [sort, setSort] = useState("signin_streak");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/friends/leaderboard?sort=${sort}`);
      setRows(data.rows || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [sort]);

  useEffect(() => {
    load();
  }, [load]);

  const valueFor = (row) => {
    if (sort === "signin_streak") return row.signin_streak;
    if (sort === "activity_streak") return row.activity_streak;
    if (sort === "workouts") return row.workouts_count;
    return row.badges_count;
  };

  const activeSort = SORTS.find((s) => s.key === sort) || SORTS[0];
  const SortIcon = activeSort.icon;

  return (
    <div
      data-testid="friends-leaderboard"
      className="bg-[#0F0F0F] border border-zinc-800 p-6 mt-6"
    >
      <div className="flex flex-wrap items-end justify-between mb-5 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
            Inner Circle Ranks
          </div>
          <h2 className="font-display text-3xl text-white">
            Leader<span className="text-[#D4AF37]">board.</span>
          </h2>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {SORTS.map((s) => {
            const Icon = s.icon;
            const on = s.key === sort;
            return (
              <button
                key={s.key}
                data-testid={`lb-sort-${s.key}`}
                onClick={() => setSort(s.key)}
                className={`text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 inline-flex items-center gap-1.5 border transition-colors ${
                  on
                    ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                    : "border-zinc-800 text-neutral-400 hover:border-zinc-600"
                }`}
              >
                <Icon size={11} />
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="text-center text-neutral-500 py-6 text-sm">
          <Loader2 size={20} className="animate-spin inline-block mr-2" />
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-neutral-500 text-center py-8">
          Add friends to see the leaderboard fire up.
        </p>
      ) : (
        <ol className="space-y-2">
          {rows.map((r) => (
            <li
              key={r.user_id}
              data-testid={`lb-row-${r.rank}`}
              className={`flex items-center gap-3 p-3 border ${
                r.is_me
                  ? "bg-[#D4AF37]/10 border-[#D4AF37]"
                  : "bg-[#0A0A0A] border-zinc-900"
              }`}
            >
              <span
                className={`flex-shrink-0 w-8 h-8 flex items-center justify-center font-display text-lg ${
                  r.rank === 1
                    ? "text-black bg-[#D4AF37]"
                    : r.rank === 2
                    ? "text-black bg-neutral-300"
                    : r.rank === 3
                    ? "text-black bg-amber-700"
                    : "text-neutral-500 border border-zinc-700"
                }`}
              >
                {r.rank}
              </span>
              {r.picture ? (
                <img
                  src={r.picture}
                  alt={r.name}
                  className="w-10 h-10 object-contain bg-black border border-zinc-800"
                />
              ) : (
                <div className="w-10 h-10 bg-[#4C1D95] border border-zinc-800 flex items-center justify-center font-display text-base text-[#D4AF37]">
                  {(r.name || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-display text-base text-white truncate">
                    {r.is_me ? "You" : r.name}
                  </span>
                  {r.role === "owner" && (
                    <span
                      className="bg-[#D4AF37] text-black text-[9px] uppercase tracking-widest px-1.5 py-0.5 inline-flex items-center gap-0.5"
                      title="Owner"
                    >
                      <Crown size={9} strokeWidth={2.5} />
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-neutral-500 uppercase tracking-widest">
                  Rank {r.rank}
                </div>
              </div>
              <div className="flex-shrink-0 inline-flex items-center gap-1.5 text-[#D4AF37] font-bold">
                <SortIcon size={14} />
                <span className="font-mono tabular-nums text-base">
                  {valueFor(r)}
                  {activeSort.suffix}
                </span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
